/**
 * After webview reload / window resurrect, attach to the connector engine session
 * without pause, stop, or play_uri (#1514 addendum 3 / N-5).
 */
import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { clearInAppNavigationReloadPending } from './transportPageTeardown';
import { reconcilePlaybackStateFromRenderer } from './nowPlayingReconcile';

function parseCurrentTransportState(rawResp: string): string {
  const match = rawResp.match(/<CurrentTransportState>(.*?)<\/CurrentTransportState>/);
  return match?.[1] || 'UNKNOWN';
}

export async function runConnectorBootMount(): Promise<void> {
  const { hydrateConnectorZoneStorageFromDisk } = await import('./connectorLastPlaybackSession');
  await hydrateConnectorZoneStorageFromDisk();
  await reattachRendererSessionOnBoot();
  const { hydrateSavedZoneOnBoot } = await import('./zoneRestorePick');
  hydrateSavedZoneOnBoot();
  const { runBootZoneRestoreWatch } = await import('./bootZoneRestoreWatch');
  void runBootZoneRestoreWatch();
}

export async function reattachRendererSessionOnBoot(): Promise<void> {
  clearInAppNavigationReloadPending();
  if (!(window as any).__TAURI__) return;

  const { reattachPlaybackSession, getTransportInfo, isConnectorAvailable } = await import('../lib/connector');
  if (!isConnectorAvailable()) return;

  try {
    const session = await reattachPlaybackSession();
    if (!session?.renderer_id) return;

    diagnosticLog('info', 'playback', 'renderer_session_reattach', {
      rendererId: session.renderer_id,
      playing: session.playing,
      queueLen: session.queue?.length ?? 0,
      positionMs: session.position_ms,
    });

    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    const rendererId = renderer?.id || session.renderer_id;

    if (session.queue?.length) {
      const queueIdx = Math.max(
        0,
        session.queue.findIndex((q) => q.id === session.track_id),
      );
      const queueRows = session.queue.map((q) => ({
        id: q.id,
        title: q.title,
        artist: session.artist || '',
      }));
      store.setQueue(queueRows as any[], queueIdx);
    }

    if (session.title || session.track_id) {
      store.setCurrentTrack({
        id: session.track_id,
        title: session.title,
        artist: session.artist,
        duration: Math.floor((session.duration_ms || 0) / 1000),
      } as any);
    }
    if (session.position_ms > 0 || session.duration_ms > 0) {
      store.updateTime(
        Math.floor(session.position_ms / 1000),
        Math.floor(session.duration_ms / 1000),
      );
    }

    if (session.playing) {
      const reconcile = reconcilePlaybackStateFromRenderer({
        localState: store.playbackState,
        rendererState: 'PLAYING',
        playUriRecentMs: false,
        inPlayUriSilenceWindow: false,
        rendererHasStream: true,
      });
      if (reconcile.action === 'set') store.setPlaybackState(reconcile.state);
      else if (store.playbackState === 'idle') store.setPlaybackState('playing');
      store.setRendererHasStream(true);
    }

    if (rendererId) {
      try {
        const raw = await getTransportInfo(rendererId);
        const rendererState = parseCurrentTransportState(raw);
        const reconcile = reconcilePlaybackStateFromRenderer({
          localState: useMaestroStore.getState().playbackState,
          rendererState,
          playUriRecentMs: false,
          inPlayUriSilenceWindow: false,
          rendererHasStream: true,
        });
        if (reconcile.action === 'set') {
          useMaestroStore.getState().setPlaybackState(reconcile.state);
        }
      } catch {
        /* transport poll may lag discovery — session snapshot is enough */
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    diagnosticLog('info', 'playback', 'renderer_session_reattach_skipped', { error: message });
  }
}
